import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "@/styles/seller-map.css";
import { cn } from "@/lib/utils";
import { usdCompact, usd, num } from "@/lib/format";
import { Search, RotateCcw, X, Copy, ExternalLink, ChevronDown, HelpCircle, SlidersHorizontal, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { cartoLightAllTileUrl, CARTO_BASEMAP_LAYER_OPTIONS } from "@/lib/cartoBasemap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const US_CENTER = [39.5, -98.35];
const US_ZOOM = 4;
const money = (v) => (v == null ? "—" : usdCompact(v));
const DEFAULT_FILTERS = { name: "", category: "All", sellerType: "All", minRev: "", maxRev: "", limit: 1000 };

function clusterIcon(cluster) {
  const c = cluster.getChildCount();
  const size = c < 10 ? 34 : c < 50 ? 42 : c < 150 ? 52 : 62;
  return L.divIcon({
    html: `<div class="ez-cluster" style="width:${size}px;height:${size}px">${c}</div>`,
    className: "ez-cluster-wrap", iconSize: L.point(size, size),
  });
}
function markerIcon(selected) {
  return L.divIcon({ className: "ez-seller-wrap", html: `<div class="ez-seller${selected ? " sel" : ""}"></div>`, iconSize: L.point(18, 18) });
}

export default function SellerMap() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const markerById = useRef({});
  const openSellerId = useRef(null);
  const reqId = useRef(0);

  const [facets, setFacets] = useState({ categories: [], sellerTypes: [] });
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [status, setStatus] = useState("idle"); // idle|loading|ok|empty|error
  const [meta, setMeta] = useState({ total: 0, returned: 0, unplaced: 0 });
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileTab, setProfileTab] = useState("details");
  const [profileLoading, setProfileLoading] = useState(false);

  // openPanel: "search" | "help" | "chart" | null — only one open at a time
  const [openPanel, setOpenPanel] = useState(null);
  const panelRef = useRef(null);
  const toggleGroupRef = useRef(null);
  const btnRefs = { search: useRef(null), help: useRef(null), chart: useRef(null) };

  const togglePanel = (panel) => setOpenPanel((cur) => (cur === panel ? null : panel));

  const safeInvalidate = () => {
    const m = mapRef.current;
    if (!m || !m._loaded || !mapEl.current?.offsetParent) return;
    try { m.invalidateSize(); } catch { /* container not ready */ }
  };

  // Escape + click-outside close the open panel and restore focus to its toggle.
  useEffect(() => {
    if (!openPanel) return;
    const onKey = (e) => {
      if (e.key === "Escape") { const p = openPanel; setOpenPanel(null); btnRefs[p]?.current?.focus(); }
    };
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (toggleGroupRef.current?.contains(e.target)) return;
      if (e.target.closest?.(".leaflet-popup") || e.target.closest?.("select")) return;
      setOpenPanel(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
    // eslint-disable-next-line
  }, [openPanel]);

  // panels are overlays; still reflow the map after any toggle just in case.
  useEffect(() => {
    const t = setTimeout(safeInvalidate, 260);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [openPanel]);

  // init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { center: US_CENTER, zoom: US_ZOOM, zoomControl: false, worldCopyJump: true });
    L.control.zoom({ position: "topright" }).addTo(map);
    L.tileLayer(cartoLightAllTileUrl(), CARTO_BASEMAP_LAYER_OPTIONS).addTo(map);
    const cluster = L.markerClusterGroup({ iconCreateFunction: clusterIcon, showCoverageOnHover: false, maxClusterRadius: 55, chunkedLoading: true });
    map.addLayer(cluster);
    map.on("popupclose", () => {
      openSellerId.current = null;
      Object.values(markerById.current).forEach((m) => m.setIcon(markerIcon(false)));
    });
    mapRef.current = map; clusterRef.current = cluster;
    setTimeout(safeInvalidate, 120);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.addEventListener("resize", safeInvalidate);
    const t = setInterval(safeInvalidate, 800);
    return () => { window.removeEventListener("resize", safeInvalidate); clearInterval(t); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetch(`${API}/seller-map/facets`).then((r) => r.json()).then((d) => setFacets(d.data || d)).catch(() => {});
  }, []);

  const renderMarkers = useCallback((rows) => {
    const cluster = clusterRef.current; if (!cluster) return;
    cluster.clearLayers(); markerById.current = {};
    const markers = rows.map((s) => {
      const mk = L.marker([s.latitude, s.longitude], { icon: markerIcon(false), title: s.sellerName, keyboard: true, alt: `${s.sellerName}, ${s.city}, ${s.state}` });
      mk.on("click", () => openPreview(s, mk));
      markerById.current[s.sellerId] = mk;
      return mk;
    });
    cluster.addLayers(markers);
  }, []);

  const openPreview = (s, mk) => {
    // Toggle: clicking the marker whose popup is already open closes it.
    if (openSellerId.current === s.sellerId) {
      mapRef.current.closePopup();
      return;
    }
    setSelected(s);
    Object.values(markerById.current).forEach((m) => m.setIcon(markerIcon(false)));
    if (mk) mk.setIcon(markerIcon(true));
    const html = `
      <div class="ez-pop" data-testid="seller-popup">
        <div class="ez-pop-name">${escapeHtml(s.sellerName)}</div>
        <div class="ez-pop-rev">${money(s.monthlyRevenue)} <span>est. monthly revenue</span></div>
        <div class="ez-pop-row">Seller ID: <b>${escapeHtml(s.sellerId)}</b></div>
        <div class="ez-pop-row">${escapeHtml([s.city, s.state].filter(Boolean).join(", ") || "—")}</div>
        <div class="ez-pop-row">${s.fulfillmentPercentage == null ? "—" : s.fulfillmentPercentage + "% WFS"} · ${escapeHtml(s.sellerType || "—")}</div>
        <button class="ez-pop-btn" data-testid="popup-view-profile">View seller profile</button>
      </div>`;
    if (mk) {
      const p = L.popup({ closeButton: true, autoPan: true, minWidth: 220, className: "ez-leaflet-pop" })
        .setLatLng([s.latitude, s.longitude])
        .setContent(html);
      p.openOn(mapRef.current); // closes any previous popup (popupclose resets icons + openSellerId)
      Object.values(markerById.current).forEach((m) => m.setIcon(markerIcon(false)));
      mk.setIcon(markerIcon(true));
      openSellerId.current = s.sellerId;
      setTimeout(() => {
        const btn = document.querySelector('[data-testid="popup-view-profile"]');
        if (btn) btn.onclick = () => openProfile(s.sellerId);
      }, 30);
    }
  };

  const doSearch = useCallback(async (override) => {
    const f = override || filters;
    if (status === "loading" && !override) return;
    const id = ++reqId.current;
    setStatus("loading");
    const p = new URLSearchParams();
    if (f.name.trim()) p.set("name", f.name.trim());
    if (f.category && f.category !== "All") p.set("category", f.category);
    if (f.sellerType && f.sellerType !== "All") p.set("seller_type", f.sellerType);
    if (f.minRev !== "") p.set("min_rev", String(Number(f.minRev)));
    if (f.maxRev !== "") p.set("max_rev", String(Number(f.maxRev)));
    p.set("limit", String(f.limit || 1000));
    try {
      const r = await fetch(`${API}/seller-map/search?${p.toString()}`);
      if (!r.ok) throw new Error("bad");
      const d = await r.json(); const data = d.data || d;
      if (id !== reqId.current) return;
      const rows = data.results || [];
      setMeta({ total: data.total, returned: data.returned, unplaced: data.unplaced });
      renderMarkers(rows);
      setStatus(rows.length ? "ok" : "empty");
      if (rows.length && clusterRef.current && mapRef.current) {
        try { mapRef.current.fitBounds(clusterRef.current.getBounds().pad(0.15)); } catch { /* ignore */ }
      }
    } catch {
      if (id !== reqId.current) return;
      setStatus("error");
    }
  }, [filters, status, renderMarkers]);

  useEffect(() => { doSearch(DEFAULT_FILTERS); /* eslint-disable-next-line */ }, []);

  const openProfile = async (sellerId) => {
    if (mapRef.current) mapRef.current.closePopup(); // fires popupclose -> resets icons + openSellerId
    const mk = markerById.current[sellerId];
    if (mk) mk.setIcon(markerIcon(true)); // keep selected marker highlighted under the drawer
    setProfile({ sellerId }); setProfileTab("details"); setProfileLoading(true);
    try {
      const r = await fetch(`${API}/seller-map/seller/${sellerId}`);
      const d = await r.json(); setProfile(d.data || d);
    } catch { toast.error("Could not load seller profile"); }
    finally { setProfileLoading(false); }
  };

  const reset = () => {
    setFilters(DEFAULT_FILTERS);
    setSelected(null); setProfile(null);
    if (mapRef.current) mapRef.current.setView(US_CENTER, US_ZOOM);
    reqId.current++;
    setTimeout(() => doSearch(DEFAULT_FILTERS), 0);
  };

  const revInvalid = filters.minRev !== "" && filters.maxRev !== "" && Number(filters.minRev) > Number(filters.maxRev);
  const onKey = (e) => { if (e.key === "Enter" && !revInvalid) doSearch(); };
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const resultLabel = status === "loading" ? "Loading…"
    : status === "ok" ? `${meta.returned} of ${meta.total} sellers${meta.unplaced ? ` · ${meta.unplaced} without location` : ""}`
    : status === "empty" ? "0 sellers" : "";

  const TOGGLES = [
    { id: "search", icon: SlidersHorizontal, label: "Search & filters" },
    { id: "help", icon: HelpCircle, label: "Help" },
    { id: "chart", icon: BarChart3, label: "Legend & stats" },
  ];

  return (
    <div className="ez-sellermap grid" style={{ gridTemplateRows: "auto minmax(0,1fr)", height: "calc(100dvh - 56px)", minWidth: 0, overflow: "hidden" }} data-testid="seller-map-page">
      <header className="px-[9px] pt-[7px] pb-2 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-stitle text-ink-primary">Seller Map</h1>
          <p className="text-compact text-ink-secondary">Discover marketplace sellers by location, revenue, category, and seller type.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-micro text-ink-muted" aria-live="polite" data-testid="map-result-count-header">{resultLabel}</span>
          <button data-testid="map-reset" onClick={reset} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2"><RotateCcw className="h-3.5 w-3.5" />Reset map</button>
        </div>
      </header>

      {/* map canvas */}
      <div className="relative mx-[9px] mb-4 rounded-panel border border-edge-subtle overflow-hidden" data-testid="seller-map-canvas" style={{ minHeight: 0 }}>
        <div ref={mapEl} className="absolute inset-0" />

        {/* top-left stacked toggles */}
        <div ref={toggleGroupRef} className="absolute left-3 top-3 z-[700] flex flex-col gap-2" data-testid="map-controls">
          {TOGGLES.map((t) => {
            const Icon = t.icon;
            const active = openPanel === t.id;
            return (
              <button
                key={t.id}
                ref={btnRefs[t.id]}
                type="button"
                data-testid={`map-toggle-${t.id}`}
                aria-label={t.label}
                aria-expanded={active}
                aria-controls={`map-panel-${t.id}`}
                title={t.label}
                onClick={() => togglePanel(t.id)}
                className={cn("ez-map-fab", active && "ez-map-fab-active")}
              >
                <Icon className="h-[18px] w-[18px]" />
              </button>
            );
          })}
        </div>

        {/* sliding panel (single, driven by openPanel) */}
        {openPanel && (
          <aside
            ref={panelRef}
            id={`map-panel-${openPanel}`}
            role="dialog"
            aria-label={TOGGLES.find((t) => t.id === openPanel)?.label}
            className="ez-map-panel"
            data-testid={`map-panel-${openPanel}`}
          >
            <div className="flex items-center justify-between gap-2 px-3 h-10 border-b border-edge-subtle">
              <span className="text-compact font-medium text-ink-primary">{TOGGLES.find((t) => t.id === openPanel)?.label}</span>
              <button data-testid="map-panel-close" aria-label="Close panel" onClick={() => { const p = openPanel; setOpenPanel(null); btnRefs[p]?.current?.focus(); }} className="h-7 w-7 grid place-items-center rounded-ctrl hover:bg-surface-2 text-ink-muted"><X className="h-4 w-4" /></button>
            </div>

            <div className="p-3 overflow-auto ez-scroll" style={{ maxHeight: "calc(100% - 40px)" }}>
              {openPanel === "search" && (
                <div className="space-y-3" role="search">
                  <label className="block">
                    <span className="text-micro font-mono uppercase text-ink-muted">Seller name</span>
                    <div className="mt-0.5 flex items-center h-9 rounded-ctrl border border-edge-subtle bg-surface-0 focus-within:ring-2 focus-within:ring-signal-blue/40">
                      <Search className="h-3.5 w-3.5 ml-2 text-ink-muted" />
                      <input data-testid="filter-name" value={filters.name} onKeyDown={onKey} onChange={(e) => set("name", e.target.value)} placeholder="Name, ID, or business" className="w-full h-full px-2 bg-transparent text-compact focus:outline-none" />
                    </div>
                  </label>
                  <Select label="Category" testid="filter-category" value={filters.category} onChange={(v) => set("category", v)} options={["All", ...facets.categories]} />
                  <Select label="Seller type" testid="filter-seller-type" value={filters.sellerType} onChange={(v) => set("sellerType", v)} options={["All", ...facets.sellerTypes]} />
                  <label className="block">
                    <span className="text-micro font-mono uppercase text-ink-muted">Monthly revenue</span>
                    <div className="mt-0.5 flex items-center gap-1">
                      <input data-testid="filter-min-rev" type="number" value={filters.minRev} onKeyDown={onKey} onChange={(e) => set("minRev", e.target.value)} placeholder="$ min" className={cn("w-full h-9 px-2 rounded-ctrl border bg-surface-0 text-compact focus:outline-none focus:ring-2 focus:ring-signal-blue/40", revInvalid ? "border-red-400" : "border-edge-subtle")} />
                      <span className="text-ink-muted">–</span>
                      <input data-testid="filter-max-rev" type="number" value={filters.maxRev} onKeyDown={onKey} onChange={(e) => set("maxRev", e.target.value)} placeholder="$ max" className={cn("w-full h-9 px-2 rounded-ctrl border bg-surface-0 text-compact focus:outline-none focus:ring-2 focus:ring-signal-blue/40", revInvalid ? "border-red-400" : "border-edge-subtle")} />
                    </div>
                    {revInvalid && <span className="text-micro text-red-500">Min revenue can’t exceed max.</span>}
                  </label>
                  <label className="block" title="Caps results returned — not total sellers in the database.">
                    <span className="text-micro font-mono uppercase text-ink-muted">Max sellers</span>
                    <input data-testid="filter-limit" type="number" value={filters.limit} onKeyDown={onKey} onChange={(e) => set("limit", Math.max(1, parseInt(e.target.value || "1000", 10)))} className="mt-0.5 w-full h-9 px-2 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
                  </label>
                  <div className="flex items-center gap-2 pt-1">
                    <button data-testid="map-search" disabled={revInvalid || status === "loading"} onClick={() => doSearch()} className={cn("flex-1 inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-ctrl text-compact font-medium", revInvalid || status === "loading" ? "bg-signal-blue/40 text-white cursor-not-allowed" : "bg-signal-blue text-white hover:bg-signal-blue/90")}><Search className="h-4 w-4" />Search</button>
                    <button data-testid="map-reset-filters" onClick={reset} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">Reset</button>
                  </div>
                  <div className="text-micro text-ink-muted pt-1" aria-live="polite" data-testid="map-result-count">{resultLabel}</div>
                </div>
              )}

              {openPanel === "help" && (
                <div className="space-y-3 text-compact text-ink-secondary" data-testid="map-help-content">
                  <p>Use the <b>Search &amp; filters</b> panel to narrow sellers by name, category, seller type, and estimated monthly revenue range.</p>
                  <ul className="list-disc pl-4 space-y-1.5">
                    <li>Numbered circles are <b>clusters</b> — click to zoom into that region.</li>
                    <li>Single dots are <b>individual sellers</b> — click for a quick preview.</li>
                    <li>Click <b>View seller profile</b> in a preview for full details, offers, and brand coverage.</li>
                    <li>Use <b>Reset map</b> to clear filters and return to the national view.</li>
                  </ul>
                  <p className="text-micro text-ink-muted">All seller data shown here is demo/fixture data.</p>
                </div>
              )}

              {openPanel === "chart" && (
                <div className="space-y-4" data-testid="map-chart-content">
                  <div>
                    <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Legend</div>
                    <div className="space-y-1.5 text-compact text-ink-secondary">
                      <div className="flex items-center gap-2"><span className="ez-legend-cluster" />Cluster (seller count)</div>
                      <div className="flex items-center gap-2"><span className="ez-legend-seller" />Individual seller</div>
                      <div className="flex items-center gap-2"><span className="ez-legend-seller sel" />Selected seller</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-micro font-mono uppercase text-ink-muted mb-1.5">Current results</div>
                    <div className="grid grid-cols-3 gap-2">
                      <Stat label="Shown" value={num(meta.returned)} />
                      <Stat label="Total" value={num(meta.total)} />
                      <Stat label="No location" value={num(meta.unplaced)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        )}

        {status === "loading" && (
          <div className="absolute inset-0 z-[600] grid place-items-center bg-white/40 backdrop-blur-sm" data-testid="map-loading">
            <div className="rounded-panel bg-surface-0/95 border border-edge-subtle shadow-float px-5 py-4 text-center">
              <div className="ez-spin mx-auto mb-2" />
              <div className="text-compact font-medium text-ink-primary">Loading sellers…</div>
              <div className="text-micro text-ink-muted">Applying filters and building map clusters.</div>
            </div>
          </div>
        )}
        {status === "empty" && (
          <div className="absolute inset-0 z-[600] grid place-items-center bg-white/50" data-testid="map-empty">
            <div className="rounded-panel bg-surface-0 border border-edge-subtle shadow-float p-5 max-w-sm text-center">
              <div className="text-compact font-medium text-ink-primary mb-1">No sellers match these filters.</div>
              <div className="text-micro text-ink-muted mb-3">Try expanding the revenue range, changing the category, or resetting the map.</div>
              <button onClick={reset} className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2">Reset filters</button>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 z-[600] grid place-items-center bg-white/50" data-testid="map-error">
            <div className="rounded-panel bg-surface-0 border border-edge-subtle shadow-float p-5 max-w-sm text-center">
              <div className="text-compact font-medium text-ink-primary mb-1">We couldn’t load seller locations.</div>
              <div className="text-micro text-ink-muted mb-3">Your filters were preserved. Try again.</div>
              <div className="flex justify-center gap-2">
                <button onClick={() => doSearch()} className="h-8 px-3 rounded-ctrl bg-signal-blue text-white text-micro">Retry</button>
                <button onClick={reset} className="h-8 px-3 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2">Reset filters</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {profile && <SellerProfile profile={profile} loading={profileLoading} tab={profileTab} setTab={setProfileTab} onClose={() => setProfile(null)} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-ctrl border border-edge-subtle bg-surface-1 px-2 py-1.5 text-center">
      <div className="ident text-title text-ink-primary tabular-nums">{value}</div>
      <div className="text-micro text-ink-muted">{label}</div>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-micro font-mono uppercase text-ink-muted">{label}</span>
      <div className="relative mt-0.5">
        <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-9 pl-2 pr-7 rounded-ctrl border border-edge-subtle bg-surface-0 text-compact appearance-none focus:outline-none focus:ring-2 focus:ring-signal-blue/40">
          {options.map((o) => <option key={o} value={o}>{o === "All" ? `- All -` : o}</option>)}
        </select>
        <ChevronDown className="h-4 w-4 absolute right-2 top-2.5 text-ink-muted pointer-events-none" />
      </div>
    </label>
  );
}

const TABS = [["details", "Details"], ["offers", "Offers"], ["brand", "Brand Coverage"], ["brandhist", "Coverage History"], ["history", "Seller History"]];

function SellerProfile({ profile, loading, tab, setTab, onClose }) {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);
  const p = profile;
  return (
    <div className="fixed inset-0 z-[1200] flex" onClick={onClose}>
      <div className="ml-auto h-full bg-surface-0 border-l border-edge-subtle shadow-float flex flex-col ez-rise-in" style={{ width: "min(760px, calc(100vw - 24px))" }} role="dialog" aria-modal="true" aria-label="Seller profile" onClick={(e) => e.stopPropagation()} data-testid="seller-profile">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-edge-subtle">
          <div className="min-w-0">
            <div className="text-stitle text-ink-primary truncate">{p.sellerName || "Seller"}</div>
            <div className="ident text-micro text-ink-muted">{p.sellerId}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button data-testid="profile-copy-id" onClick={() => { navigator.clipboard?.writeText(p.sellerId); toast.success("Seller ID copied"); }} className="h-8 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2 inline-flex items-center gap-1"><Copy className="h-3.5 w-3.5" />ID</button>
            {p.marketplaceUrl && <a href={p.marketplaceUrl} target="_blank" rel="noopener noreferrer" className="h-8 px-2 rounded-ctrl border border-edge-subtle text-micro hover:bg-surface-2 inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" />Marketplace</a>}
            <button data-testid="profile-close" onClick={onClose} aria-label="Close seller profile" className="h-8 w-8 grid place-items-center rounded-ctrl border border-edge-subtle hover:bg-surface-2"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="px-4 pt-2 border-b border-edge-subtle flex gap-1 overflow-x-auto ez-scroll">
          {TABS.map(([id, lbl]) => (
            <button key={id} data-testid={`profile-tab-${id}`} onClick={() => setTab(id)} className={cn("px-3 h-9 text-compact whitespace-nowrap border-b-2 -mb-px", tab === id ? "border-signal-blue text-signal-blue font-medium" : "border-transparent text-ink-muted hover:text-ink-primary")}>{lbl}</button>
          ))}
        </div>

        <div className="flex-1 overflow-auto ez-scroll p-4" data-testid="profile-body">
          {loading ? <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-8 rounded bg-surface-2 animate-pulse" />)}</div>
            : tab === "details" ? <Details p={p} />
            : tab === "offers" ? <Table cols={["Title", "ASIN", "Brand", "Category", "Price", "Est. sales", "Est. revenue", "Fulfillment", "Stock"]} rows={(p.offers || []).map((o) => [o.title, o.asin, o.brand, o.category, usd(o.price), num(o.estMonthlySales), money(o.estMonthlyRevenue), o.fulfillment, num(o.stock)])} empty="No offers available." />
            : tab === "brand" ? <Table cols={["Brand", "Products", "Est. revenue", "Coverage", "First seen", "Last seen"]} rows={(p.brandCoverage || []).map((b) => [b.brand, num(b.products), money(b.estMonthlyRevenue), `${b.coverage}%`, b.firstObserved, b.lastObserved])} empty="No brand coverage available." />
            : tab === "brandhist" ? <Table cols={["Date", "Brand", "Products", "Coverage", "Change"]} rows={(p.brandCoverageHistory || []).map((h) => [h.date, h.brand, num(h.productCount), `${h.coverage}%`, h.change])} empty="No coverage history available." />
            : <Table cols={["Date", "Seller", "Business", "Address", "Fulfillment", "Revenue", "Status"]} rows={(p.sellerHistory || []).map((h) => [h.date, h.sellerName, h.businessName, h.address, `${h.fulfillmentPercentage}%`, money(h.revenue), h.status])} empty="No seller history available." />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return <div><div className="text-micro font-mono uppercase text-ink-muted">{label}</div><div className="text-compact text-ink-primary">{value == null || value === "" ? "—" : value}</div></div>;
}
function Details({ p }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Monthly revenue" value={money(p.monthlyRevenue)} />
        <Field label="Fulfillment" value={p.fulfillmentPercentage == null ? "—" : `${p.fulfillmentPercentage}% WFS`} />
        <Field label="Brand coverage" value={p.brandCount == null ? "—" : `${p.brandCount} brands`} />
        <Field label="Seller type" value={p.sellerType} />
        <Field label="Business name" value={p.businessName} />
        <Field label="Suspended" value={p.suspended ? `Yes${p.lastSuspended ? ` (${p.lastSuspended})` : ""}` : "No"} />
        <Field label="Current address" value={p.addressHistory?.[0] ? `${p.addressHistory[0].street}, ${p.city}, ${p.state}` : "—"} />
      </div>
      <div>
        <div className="text-micro font-mono uppercase text-ink-muted mb-1">Address history</div>
        <Table cols={["Street", "City", "State", "ZIP", "Country", "Date"]} rows={(p.addressHistory || []).map((a) => [a.street, a.city, a.state, a.zip, a.country, a.date])} empty="No address history." />
      </div>
    </div>
  );
}
function Table({ cols, rows, empty }) {
  if (!rows.length) return <div className="text-compact text-ink-muted py-8 text-center">{empty}</div>;
  return (
    <div className="overflow-x-auto ez-scroll border border-edge-subtle rounded-ctrl">
      <table className="w-full text-compact" style={{ minWidth: cols.length * 110 }}>
        <thead><tr className="text-micro text-ink-muted text-left bg-surface-2">{cols.map((c) => <th key={c} className="px-3 py-2 border-b border-edge-subtle whitespace-nowrap">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="hover:bg-surface-1">{r.map((v, j) => <td key={j} className="px-3 py-2 border-b border-edge-subtle whitespace-nowrap">{v == null || v === "" ? "—" : v}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function escapeHtml(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])); }
